import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api, { fmtErr } from "../lib/api";
import { toast } from "sonner";
import { NotificationBell } from "./NotificationBell";
import { BookOpen, LogOut, Eye } from "lucide-react";

const authorLinks = [
  { to: "/dashboard", label: "My Books" },
  { to: "/projects/new", label: "New Submission" },
  { to: "/sales", label: "Sales & Royalties" },
  { to: "/my-store", label: "My Store" },
  { to: "/marketing", label: "Marketing" },
  { to: "/profile", label: "Profile" },
  { to: "/help", label: "Help" },
];

const adminLinks = [
  { to: "/admin", label: "Pipeline", end: true },
  { to: "/admin/imports", label: "Sales Imports" },
  { to: "/admin/payouts", label: "Payouts" },
  { to: "/admin/users", label: "Users" },
  { to: "/admin/store", label: "Bookstore" },
  { to: "/admin/website", label: "Website" },
  { to: "/admin/reports", label: "Reports" },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isStaff = user && (user.role === "admin" || user.role === "super_admin");
  const links = isStaff
    ? [...adminLinks, ...(user.role === "super_admin" ? [{ to: "/admin/helpdesk", label: "Helpdesk" }, { to: "/desk", label: "Support Desk" }, { to: "/admin/settings", label: "Settings" }] : [])]
    : authorLinks;

  const stopImpersonation = async () => {
    try {
      await api.post("/admin/stop-impersonation");
      window.location.assign("/admin/users");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const [membership, setMembership] = useState(null);
  useEffect(() => {
    if (user && user.role === "author") {
      api.get("/membership/my").then((r) => setMembership(r.data)).catch(() => {});
    }
  }, [user]);

  return (
    <div className="min-h-screen bg-neutral-50">
      {user?.impersonated && (
        <div data-testid="impersonation-banner" className="bg-amber-600 text-white text-sm font-medium px-4 py-2 flex items-center justify-center gap-3 sticky top-0 z-50">
          <Eye size={15} />
          <span>Viewing as <b>{user.name}</b> ({user.email}) — impersonation mode</span>
          <button data-testid="stop-impersonation-button" onClick={stopImpersonation}
            className="bg-white text-amber-700 px-3 py-0.5 rounded-md text-xs font-semibold hover:bg-amber-50">
            Return to admin
          </button>
        </div>
      )}
      {membership && (membership.status === "expired" || membership.status === "expiring") && (
        <div data-testid="membership-banner" className={`${membership.status === "expired" ? "bg-red-600" : "bg-amber-500"} text-white text-sm font-medium px-4 py-2 flex items-center justify-center gap-3`}>
          <span>
            {membership.status === "expired"
              ? "Your monthly membership has lapsed — renew to maintain full access to your dashboard, sales and payouts."
              : `Your membership renews soon — ${membership.days_left} day${membership.days_left === 1 ? "" : "s"} of access left.`}
          </span>
          <Link to="/profile" data-testid="membership-banner-renew"
            className="bg-white text-neutral-900 px-3 py-0.5 rounded-md text-xs font-semibold hover:bg-neutral-100">
            Renew ${membership.monthly_price?.toFixed(2)}/mo
          </Link>
        </div>
      )}      <header className="bg-white/80 backdrop-blur-xl border-b border-neutral-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <Link to={isStaff ? "/admin" : "/dashboard"} className="flex items-center gap-2 font-bold text-lg text-neutral-900 font-heading tracking-tight" data-testid="nav-logo">
              <BookOpen size={21} strokeWidth={2.4} />
              GloriPub
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      isActive ? "bg-neutral-900 text-white" : "text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <span className="text-sm text-neutral-500 hidden sm:block" data-testid="nav-user-name">
              {user?.name}
              {isStaff && (
                <span className="ml-2 text-xs font-semibold uppercase tracking-wider bg-neutral-900 text-white px-2 py-0.5 rounded">
                  {user.role === "super_admin" ? "Super Admin" : "Admin"}
                </span>
              )}
            </span>
            <button
              data-testid="logout-button"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="p-2 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 rounded-md transition-colors"
              title="Log out"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
        <nav className="md:hidden flex overflow-x-auto px-4 pb-2 gap-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end}
              className={({ isActive }) => `px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap ${isActive ? "bg-neutral-900 text-white font-medium" : "text-neutral-500"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</main>
    </div>
  );
};
